import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Globe, 
  Sparkles,
  Droplets,
  TreePine,
  Snowflake,
  CheckCircle2,
  ChevronDown,
  Cookie,
  X,
  Shield,
  ArrowLeft,
  FileText,
  Scale
} from "lucide-react";

// Assets - Lokal eingebunden (DSGVO-konform)
const LOGO_URL = "/images/logo.png";
const HERO_IMAGE = "/images/hero.jpg";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Navigation Component
const Navigation = ({ isLegalPage = false }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // On legal pages, always show scrolled style
  const showScrolledStyle = scrolled || isLegalPage;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showScrolledStyle 
          ? "bg-white/95 backdrop-blur-md shadow-lg py-3" 
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img 
            src={LOGO_URL} 
            alt="Objektbetreuung Friedel Logo" 
            className={`h-12 md:h-14 transition-all duration-300 ${showScrolledStyle ? '' : 'brightness-0 invert'}`}
          />
        </Link>
        <div className="hidden md:flex items-center gap-8">
          {[
            { id: "services", label: "Leistungen" },
            { id: "about", label: "Über Uns" },
            { id: "contact", label: "Kontakt" }
          ].map((item) => (
            isLegalPage ? (
              <Link
                key={item.id}
                to={`/#${item.id}`}
                data-testid={`nav-${item.id}`}
                className={`font-body font-medium transition-colors animated-underline ${
                  showScrolledStyle ? "text-navy hover:text-orange" : "text-white hover:text-orange"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className={`font-body font-medium transition-colors animated-underline ${
                  showScrolledStyle ? "text-navy hover:text-orange" : "text-white hover:text-orange"
                }`}
              >
                {item.label}
              </button>
            )
          ))}
        </div>
        <a
          href="tel:+4917470890047"
          data-testid="nav-phone-btn"
          className="hidden md:flex items-center gap-2 bg-orange hover:bg-orange/90 text-white px-5 py-2.5 rounded-full font-body font-medium transition-all btn-primary"
        >
          <Phone className="w-4 h-4" />
          <span>Anrufen</span>
        </a>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={HERO_IMAGE} 
          alt="Professionelles Team vor Alpenkulisse" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/80 to-navy/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
        <div className="max-w-3xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {/* Tagline */}
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/90 font-body text-sm">
                <Sparkles className="w-4 h-4 text-orange" />
                Deutschland & Österreich
              </span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1 
              variants={fadeInUp}
              className="font-heading font-black text-5xl md:text-7xl text-white leading-none tracking-tight mb-6"
            >
              WERTERHALT<br />
              <span className="text-orange">AUF HÖCHSTEM</span><br />
              NIVEAU
            </motion.h1>

            {/* Subheading */}
            <motion.p 
              variants={fadeInUp}
              className="font-body text-lg md:text-xl text-white/80 leading-relaxed mb-10 max-w-xl"
            >
              Erstklassige Objektbetreuung für exklusive Anwesen und Firmengebäude 
              in Deutschland & Österreich.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+4917470890047"
                data-testid="hero-cta-phone"
                className="inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange/90 text-white px-8 py-4 rounded-full font-body font-semibold text-lg transition-all btn-primary"
              >
                <Phone className="w-5 h-5" />
                +49 (0) 174 70 89 047
              </a>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full font-body font-semibold text-lg transition-all border border-white/20"
              >
                Unsere Leistungen
                <ChevronDown className="w-5 h-5" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ChevronDown className="w-8 h-8" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Service Card Component
const ServiceCard = ({ icon: Icon, title, description, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="service-card bg-white rounded-2xl p-8 shadow-lg border border-gray-100 group"
      data-testid={`service-card-${index}`}
    >
      <div className="w-16 h-16 bg-navy/5 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange/10 transition-colors">
        <Icon className="w-8 h-8 text-navy group-hover:text-orange transition-colors" />
      </div>
      <h3 className="font-heading font-bold text-lg md:text-xl text-navy mb-3">{title}</h3>
      <p className="font-body text-sm text-muted-foreground leading-relaxed">{description}</p>
    </motion.div>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Sparkles,
      title: "Unterhaltsreinigung",
      description: "Präzise Pflege für Ihre Innenräume und Geschäftsflächen. Tägliche bis wöchentliche Reinigung nach höchsten Standards."
    },
    {
      icon: Droplets,
      title: "Glasreinigung",
      description: "Streifenfreier Glanz für anspruchsvolle Architektur. Kristallklare Sicht für repräsentative Gebäude."
    },
    {
      icon: TreePine,
      title: "Grundstückspflege",
      description: "Ganzheitliche Betreuung Ihrer Außenanlagen und Gärten. Professionelle Gartenpflege in Perfektion."
    },
    {
      icon: Snowflake,
      title: "Spezialservice",
      description: "Individuelle Lösungen – vom Winterdienst bis zur Individualreinigung. Sicherheit bei Schnee und Eis rund um die Uhr."
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span 
            variants={fadeInUp}
            className="inline-block bg-orange/10 text-orange px-4 py-2 rounded-full font-body font-medium text-sm mb-4"
          >
            Unsere Leistungen
          </motion.span>
          <motion.h2 
            variants={fadeInUp}
            className="font-heading font-bold text-4xl md:text-5xl text-navy mb-4"
          >
            Umfassende Objektbetreuung
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            Von der täglichen Reinigung bis zum Winterdienst – wir bieten Ihnen das komplette Spektrum 
            professioneller Gebäudebetreuung.
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const highlights = [
    {
      title: "Höchste Qualitätsstandards",
      description: "Einsatz modernster Technik und umweltschonender Reinigungsmittel."
    },
    {
      title: "Individuelle Konzepte",
      description: "Maßgeschneiderte Servicepläne, die exakt auf Ihr Objekt abgestimmt sind."
    },
    {
      title: "Regionale Kompetenz",
      description: "Zuverlässige Betreuung vor Ort im gesamten deutsch-österreichischen Alpenraum."
    }
  ];

  return (
    <section id="about" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div ref={ref} className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block bg-navy/5 text-navy px-4 py-2 rounded-full font-body font-medium text-sm mb-6">
              Über Uns
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-navy mb-6 leading-tight">
              Ihr Partner für exzellente Immobilienservices
            </h2>
            <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
              <p>
                Objektbetreuung Friedel ist Ihr erfahrener Partner für exzellente 
                Immobilienservices im Alpenraum. Wir haben uns auf die Betreuung 
                hochklassiger Anwesen und moderner Firmengebäude spezialisiert, 
                bei denen Präzision und Diskretion an erster Stelle stehen.
              </p>
              <p>
                Unser Anspruch ist es, nicht nur für Sauberkeit zu sorgen, sondern 
                den Wert und die Ästhetik Ihrer Immobilie langfristig zu sichern. 
                Mit Liebe zum Detail und handwerklicher Perfektion setzen wir 
                Maßstäbe in der Gebäudereinigung – in Deutschland wie in Österreich.
              </p>
            </div>
          </motion.div>

          {/* Right - Highlights */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.15 }}
                className="flex gap-4 p-6 bg-muted/50 rounded-2xl hover:bg-muted transition-colors"
                data-testid={`highlight-${index}`}
              >
                <div className="flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-orange" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-navy mb-1">
                    {item.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-sm">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Footer
const ContactFooter = () => {
  const contactInfo = [
    {
      icon: Phone,
      label: "Telefon",
      value: "+49 (0) 174 70 89 047",
      href: "tel:+4917470890047"
    },
    {
      icon: Mail,
      label: "E-Mail",
      value: "info@objektbetreuung-friedel.de",
      href: "mailto:info@objektbetreuung-friedel.de"
    },
    {
      icon: Globe,
      label: "Webseite",
      value: "www.objektbetreuung-friedel.de",
      href: "https://www.objektbetreuung-friedel.de"
    },
    {
      icon: MapPin,
      label: "Adresse",
      value: "Am Steinbruch 10, 97234 Reichenberg, DE",
      href: "https://maps.google.com/?q=Am+Steinbruch+10,+97234+Reichenberg"
    }
  ];

  return (
    <footer id="contact" className="bg-navy text-white py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Brand Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src={LOGO_URL} 
              alt="Objektbetreuung Friedel" 
              className="h-16 mb-6 brightness-0 invert"
            />
            <p className="font-body text-white/70 leading-relaxed max-w-md mb-8">
              Erstklassige Objektbetreuung für exklusive Anwesen und Firmengebäude 
              in Deutschland & Österreich. Vertrauen Sie auf unsere Expertise im 
              Alpenraum.
            </p>
            <div className="section-divider"></div>
          </motion.div>

          {/* Right - Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="font-heading font-bold text-2xl mb-8">Kontakt</h3>
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  target={item.icon === Globe || item.icon === MapPin ? "_blank" : undefined}
                  rel={item.icon === Globe || item.icon === MapPin ? "noopener noreferrer" : undefined}
                  data-testid={`contact-${item.label.toLowerCase()}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center group-hover:bg-orange transition-colors">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="block font-body text-sm text-white/50 mb-1">
                      {item.label}
                    </span>
                    <span className="font-body text-white group-hover:text-orange transition-colors">
                      {item.value}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="font-body text-sm text-white/50">
              © {new Date().getFullYear()} Objektbetreuung Friedel. Alle Rechte vorbehalten.
            </p>
            
            {/* Legal Links */}
            <div className="flex items-center gap-6">
              <Link 
                to="/impressum" 
                className="font-body text-sm text-white/50 hover:text-orange transition-colors"
                data-testid="footer-impressum-link"
              >
                Impressum
              </Link>
              <Link 
                to="/datenschutz" 
                className="font-body text-sm text-white/50 hover:text-orange transition-colors"
                data-testid="footer-datenschutz-link"
              >
                Datenschutzerklärung
              </Link>
            </div>
            
            {/* Credit */}
            <a 
              href="https://wildmedia.eu" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-body text-sm text-white/50 hover:text-orange transition-colors"
              data-testid="footer-credit-link"
            >
              Umsetzung & Design: Wild Media GmbH
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Cookie Banner Component - DSGVO konform
const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      functional: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      functional: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      data-testid="cookie-banner"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Main Banner */}
        <div className="p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-navy/5 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-navy" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-xl text-navy mb-2">
                Datenschutz-Einstellungen
              </h3>
              <p className="font-body text-muted-foreground text-sm leading-relaxed mb-4">
                Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                Notwendige Cookies sind für das Funktionieren der Website erforderlich. 
                Funktionale Cookies helfen uns, Ihre Präferenzen zu speichern.
              </p>
              
              {/* Details Toggle */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm font-body text-navy hover:text-orange transition-colors underline mb-4"
                data-testid="cookie-details-toggle"
              >
                {showDetails ? "Details ausblenden" : "Details anzeigen"}
              </button>

              {/* Details Section */}
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mb-4 p-4 bg-muted/50 rounded-xl"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-body font-medium text-navy text-sm">Notwendige Cookies</p>
                        <p className="font-body text-muted-foreground text-xs">
                          Erforderlich für grundlegende Website-Funktionen
                        </p>
                      </div>
                      <span className="text-xs font-body text-green-600 bg-green-50 px-2 py-1 rounded">
                        Immer aktiv
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-body font-medium text-navy text-sm">Funktionale Cookies</p>
                        <p className="font-body text-muted-foreground text-xs">
                          Speichern Ihre Einstellungen und Präferenzen
                        </p>
                      </div>
                      <span className="text-xs font-body text-muted-foreground bg-gray-100 px-2 py-1 rounded">
                        Optional
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={acceptNecessary}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-navy font-body font-medium rounded-full transition-colors text-sm"
                  data-testid="cookie-accept-necessary"
                >
                  Nur Notwendige
                </button>
                <button
                  onClick={acceptAll}
                  className="px-6 py-3 bg-orange hover:bg-orange/90 text-white font-body font-medium rounded-full transition-colors text-sm btn-primary"
                  data-testid="cookie-accept-all"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
            
            {/* Close Button */}
            <button
              onClick={acceptNecessary}
              className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Schließen"
              data-testid="cookie-close"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Scroll to Top Component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Legal Page Header
const LegalPageHeader = ({ title, icon: Icon }) => {
  return (
    <div className="bg-navy pt-32 pb-16">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/70 hover:text-orange transition-colors mb-8 font-body"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zur Startseite
        </Link>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center">
            <Icon className="w-8 h-8 text-orange" />
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-5xl text-white">{title}</h1>
        </div>
      </div>
    </div>
  );
};

// Impressum Page
const ImpressumPage = () => {
  return (
    <div data-testid="impressum-page">
      <Navigation isLegalPage={true} />
      <LegalPageHeader title="Impressum" icon={FileText} />
      <div className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none font-body text-foreground">
            
            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                Angaben gemäß § 5 TMG
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Objektbetreuung Friedel<br />
                Am Steinbruch 10<br />
                97234 Reichenberg
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <strong className="text-navy">Inhaber:</strong> Michael Friedel (Einzelunternehmen)
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                Kontakt
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Telefon: <a href="tel:+4917470890047" className="text-orange hover:underline">+49 (0) 174 70 89 047</a><br />
                E-Mail: <a href="mailto:info@objektbetreuung-friedel.de" className="text-orange hover:underline">info@objektbetreuung-friedel.de</a><br />
                Webseite: <a href="https://www.objektbetreuung-friedel.de" target="_blank" rel="noopener noreferrer" className="text-orange hover:underline">www.objektbetreuung-friedel.de</a>
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                Umsatzsteuer-ID
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Die Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz ist beantragt 
                und wird nach Erhalt umgehend nachgetragen.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                Redaktionell verantwortlich
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Michael Friedel<br />
                Am Steinbruch 10<br />
                97234 Reichenberg
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                EU-Streitschlichtung
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
                <a 
                  href="https://ec.europa.eu/consumers/odr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange hover:underline break-all"
                >
                  https://ec.europa.eu/consumers/odr/
                </a>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-2">
                Unsere E-Mail-Adresse finden Sie oben im Impressum.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                Verbraucherstreitbeilegung / Universalschlichtungsstelle
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
                Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </section>

          </div>
        </div>
      </div>
      <ContactFooter />
    </div>
  );
};

// Datenschutz Page
const DatenschutzPage = () => {
  return (
    <div data-testid="datenschutz-page">
      <Navigation isLegalPage={true} />
      <LegalPageHeader title="Datenschutzerklärung" icon={Scale} />
      <div className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none font-body text-foreground">
            
            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                1. Datenschutz auf einen Blick
              </h2>
              <h3 className="font-heading font-bold text-xl text-navy mb-3 mt-6">Allgemeine Hinweise</h3>
              <p className="text-muted-foreground leading-relaxed">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
                Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit 
                denen Sie persönlich identifiziert werden können.
              </p>
              <h3 className="font-heading font-bold text-xl text-navy mb-3 mt-6">Datenerfassung auf dieser Website</h3>
              <p className="text-muted-foreground leading-relaxed">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                können Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" in dieser Datenschutzerklärung entnehmen.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                2. Hosting
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Wir hosten die Inhalte unserer Website bei folgendem Anbieter:
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Wild Media GmbH<br />
                Sieboldstraße 7<br />
                97688 Bad Kissingen<br />
                Deutschland
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Bei jedem Aufruf der Website erfasst unser Hoster automatisch Informationen in sogenannten 
                Server-Log-Dateien, die Ihr Browser an uns übermittelt (z. B. IP-Adresse, Browsertyp, Referrer URL, 
                Uhrzeit der Serveranfrage). Dies ist technisch notwendig, um die Website stabil und sicher anzuzeigen 
                (Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO). Wir haben mit dem Anbieter einen Vertrag über 
                Auftragsverarbeitung (AVV) geschlossen, um den Schutz Ihrer Daten zu gewährleisten.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                3. Allgemeine Hinweise und Pflichtinformationen
              </h2>
              <h3 className="font-heading font-bold text-xl text-navy mb-3 mt-6">Verantwortliche Stelle</h3>
              <p className="text-muted-foreground leading-relaxed">
                Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Michael Friedel<br />
                Am Steinbruch 10<br />
                97234 Reichenberg<br /><br />
                E-Mail: <a href="mailto:info@objektbetreuung-friedel.de" className="text-orange hover:underline">info@objektbetreuung-friedel.de</a><br />
                Telefon: <a href="tel:+4917470890047" className="text-orange hover:underline">+49 (0) 174 70 89 047</a>
              </p>
              <h3 className="font-heading font-bold text-xl text-navy mb-3 mt-6">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
              <p className="text-muted-foreground leading-relaxed">
                Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können 
                eine bereits erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf 
                erfolgten Datenverarbeitung bleibt vom Widerruf unberührt.
              </p>
              <h3 className="font-heading font-bold text-xl text-navy mb-3 mt-6">Beschwerderecht bei der zuständigen Aufsichtsbehörde</h3>
              <p className="text-muted-foreground leading-relaxed">
                Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei einer 
                Aufsichtsbehörde zu.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                4. Datenerfassung auf dieser Website
              </h2>
              <h3 className="font-heading font-bold text-xl text-navy mb-3 mt-6">Cookies</h3>
              <p className="text-muted-foreground leading-relaxed">
                Unsere Website verwendet Cookies. Das sind kleine Textdateien, die Ihr Browser auf Ihrem Endgerät 
                speichert. Wir nutzen einen Cookie-Disclaimer (Einwilligungs-Banner), um Ihre Zustimmung zur 
                Speicherung bestimmter Cookies einzuholen. Wir verzichten auf externe Tracking- oder Analyse-Cookies, 
                die über das technisch notwendige Maß hinausgehen.
              </p>
              <h3 className="font-heading font-bold text-xl text-navy mb-3 mt-6">Kontakt per E-Mail oder Telefon</h3>
              <p className="text-muted-foreground leading-relaxed">
                Wenn Sie uns per E-Mail oder Telefon kontaktieren, werden Ihre Angaben inklusive der von Ihnen 
                dort angegebenen personenbezogenen Daten zum Zwecke der Bearbeitung der Anfrage und für den Fall 
                von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter 
                (Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO).
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                5. Plugins und Tools
              </h2>
              <h3 className="font-heading font-bold text-xl text-navy mb-3 mt-6">Google Web Fonts (Lokale Einbindung)</h3>
              <p className="text-muted-foreground leading-relaxed">
                Diese Seite nutzt zur einheitlichen Darstellung von Schriftarten sogenannte Web Fonts, die von 
                Google bereitgestellt werden. Um Ihre Privatsphäre zu schützen, haben wir diese Schriftarten 
                lokal auf unserem eigenen Server installiert.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Das bedeutet: Beim Laden der Seite wird keine Verbindung zu Servern von Google hergestellt. 
                Es findet somit keine Datenübertragung (insbesondere Ihrer IP-Adresse) an Google in die USA statt.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                6. Ihre Rechte (Betroffenenrechte)
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Sie haben jederzeit das Recht:
              </p>
              <ul className="list-disc pl-6 mt-4 text-muted-foreground space-y-2">
                <li>unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten.</li>
                <li>die Berichtigung oder Löschung dieser Daten zu verlangen.</li>
                <li>die Einschränkung der Verarbeitung zu fordern.</li>
                <li>Widerspruch gegen die Verarbeitung einzulegen.</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="font-heading font-bold text-2xl text-navy mb-4">
                7. Aktualität und Änderung dieser Datenschutzerklärung
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen 
                rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der 
                Datenschutzerklärung umzusetzen.
              </p>
            </section>

          </div>
        </div>
      </div>
      <ContactFooter />
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
      </main>
      <ContactFooter />
    </>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="App" data-testid="app-container">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
        </Routes>
        <CookieBanner />
      </div>
    </BrowserRouter>
  );
}

export default App;
